import { Button, cn } from "web-design-system";
import React, { useCallback, useMemo } from "react";

import { useRouter } from "@tanstack/react-router";

import { Route as ClientRoute } from "@/routes/(auth)/_headed/clients/$id";
import { TransactionTrigger } from "@core/components/transaction-trigger";
import {
  useInvestorBankAccounts,
  usePortfolioSummaryV2,
} from "creso-core/hooks";
import { Page } from "@core/components/layout/page";
import { EmptyExternalPortfolio } from "@crm/components/portfolio/no-external-holdings";
import { isActiveClient } from "creso-core/utils";
import { PortfolioDetails } from "../components/portfolio/details";

export const Portfolio: React.FC = () => {
  const router = useRouter();
  const { client } = ClientRoute.useLoaderData();

  const { data: summary } = usePortfolioSummaryV2(
    client?.investment_account ?? ""
  );

  const overallSummary = summary?.find((p) => p.source_type === "OVERALL");

  const hasAum = Number(overallSummary?.invested_value) > 0;

  const showModal = useCallback(
    (modal: "transaction" | "mandate" | "proposal") => {
      router.navigate({
        to: ".",
        search: (prev) => {
          return { ...prev, modal, client_id: client?.id };
        },
      });
    },
    [router, client]
  );

  const { data: bankAccounts } = useInvestorBankAccounts(
    client.investment_account ?? ""
  );

  const hasAtleastOneVerifiedBankAccount = useMemo(
    () => bankAccounts?.some((b) => b.verification_status === "verified"),
    [bankAccounts]
  );

  const canClientTransact =
    (client.status === "ACTIVE" || client.status === "UPDATE_PENDING") &&
    hasAtleastOneVerifiedBankAccount;

  const showEmptyState = !hasAum && !isActiveClient(client);

  return (
    <>
      <Page.Header
        className="relative"
        title={showEmptyState ? "Mutual Funds" : "Mutual Funds Portfolio"}
        actions={
          <>
            <Button
              onClick={() => {
                showModal("mandate");
              }}
              className={cn(canClientTransact ? "flex" : "hidden")}
              variant="outline"
            >
              Mandates
            </Button>
            <TransactionTrigger
              className={cn(canClientTransact ? "flex" : "hidden")}
              variant="default"
              label="Transaction"
              clientId={client.id}
            />
          </>
        }
      />
      <Page.Body className="flex flex-col gap-4">
        {showEmptyState ? (
          <EmptyExternalPortfolio client={client} />
        ) : (
          <>
            <PortfolioDetails />
          </>
        )}
      </Page.Body>
    </>
  );
};
